import { TZDate } from "@date-fns/tz";
import ToyotaAdBanner from "app/ad-banner/ToyotaAdBanner";
import flowers from "app/assets/flowers.png";
// import NPSFeedbackDialog from "app/feedback/NPSFeedbackDialog";
import useGameData from "app/game-data/useGameData";
import GameStreakModal from "app/game-over/GameStreakModal";
import NewsletterSignupBanner from "app/game-over/NewsletterSignupBanner";
import ShareButton from "app/game-over/ShareButton";
import useVisitCounter from "app/game-over/useVisitCounter";
import useGameState, {
  GameAction,
  useGameActionDispatch,
} from "app/game/useGameState";
import IconPlay from "app/icons/IconPlay";
import ExitGameButton from "app/top-nav/ExitGameButton";
import CountDownTimer from "app/ui/CountDownTimer";
import MenuButton from "app/ui/MenuButton";
import cn from "app/util/classnames";
import extractTedVideoSlug from "app/util/extractTedVideoSlug";
import { formatGameTime, getTimeDescriptor } from "app/util/timeUtils";
import { useFetchTedTalkMetadata } from "app/util/useFetchTedTalkMetadata";
import mixpanel from "mixpanel-browser";
import { useEffect } from "react";

const SAILTHRU_CUSTOMER_ID = import.meta.env.VITE_SAILTHRU_CUSTOMER_ID;

interface GameOverProps {
  onViewCompletedGrid: () => void;
}

export default function GameOver(props: GameOverProps) {
  const gameData = useGameData();
  const gameDispatch = useGameActionDispatch();
  const today = new TZDate(new Date(), "America/New_York");
  const { gameTime, gameStreakComplete/*, id: gameId */ } = useGameState();
  const formattedGameTime = formatGameTime(gameTime);
  const videoUrl = gameData?.level?.originalVideo ?? "";
  // Leaving this here for debug purposes
  // const videoUrl =
  //   "https://www.ted.com/talks/max_g_levy_the_machine_that_changed_our_understanding_of_human_history";

  const {
    loading: videoMetadataLoading,
    title: videoTitle,
    viewedCount: videoViewCount,
  } = useFetchTedTalkMetadata(extractTedVideoSlug(videoUrl));

  const { visitCount, reachedLimit } = useVisitCounter({
    storeKey: "SHARE_PAGE_GAME_VISITS",
    maxVisits: 4,
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.Sailthru?.init({
      customerId: SAILTHRU_CUSTOMER_ID,
      hostWebsiteType: "spa",
      overlayKeyValue: {
        round: "3",
        "share-page": "true",
        visit: reachedLimit ? `4+` : `${visitCount}`,
        "pixellence-reminders-banner": visitCount < 3 ? "true" : "false",
      },
    });
  }, [reachedLimit, visitCount]);

  if (!gameStreakComplete) {
    return (
      <GameStreakModal
        onClose={() => {
          gameDispatch({
            type: GameAction.GAME_STREAK_COMPLETED,
          });
        }}
      />
    );
  }

  function handleBackToGridClick() {
    props.onViewCompletedGrid();
  }

  return (
    <div
      className={cn(
        "z-0",
        "flex h-dvh w-dvw flex-col",
        "overflow-x-clip overflow-y-auto",
        "bg-green-mid text-dark",
      )}
    >
      <ExitGameButton fillColor="#000000" hideText />
      <div // Main content container
        className={cn(
          "relative h-full",
          "flex grow flex-col items-center justify-start gap-1.5 md:gap-5 text-center",
          "px-5",
        )}
      >
        <ToyotaAdBanner showDisclaimer={false} />
        <img // Background flowers image
          className={cn(
            "absolute -z-10 top-12 md:-top-11 max-w-100 md:max-w-none scale-125 md:scale-83 opacity-80",
            "mask-[radial-gradient(ellipse_50%_100%_at_center,transparent_10%,black_90%)]",
            "md:mask-[radial-gradient(ellipse_100%_50%_at_center,transparent_10%,black_90%)]",
          )}
          src={flowers}
        />
        <h1 className={"font-condensed uppercase text-3xl"}>Well Done!</h1>
        <div // Responsive container
          className="flex flex-col gap-2 md:gap-3 md:flex-row md:justify-center md:items-center mb-1 md:mb-3"
        >
          <div // TED Image & link section
            className={cn(
              "min-w-60 max-w-80 md:max-w-78 p-1 md:p-2",
              "bg-blue-light shadow-[0_1px_5px_2px_rgba(0,0,0,0.1)]",
              "flex flex-col items-center",
            )}
          >
            <div className="bg-blue-light flex flex-col items-center">
              <h2 className="font-condensed md:text-lg">
                {gameData?.level?.name ?? ""}
              </h2>
              <div // Image container
                className={cn(
                  "mt-1 mb-0 md:mb-1.5",
                  "flex justify-center items-center",
                  "w-fit",
                  "overflow-hidden",
                  "group",
                  // Inner outline is used to cover black lines that frequenly
                  // line the edges of these screenshots
                  "outline-3 outline-blue-300 hover:outline-blue-200 active:outline-blue-200 -outline-offset-2 rounded-md",
                  "shadow-[0_1px_3px_2px_rgba(0,0,0,0.1)]",
                )}
              >
                <a href={videoUrl}>
                  <img // TED image
                    src={gameData?.level?.originalScreenshotUrl}
                    className={cn(
                      "bg-red-400",
                      "aspect-auto object-contain",
                      "h-full max-h-50 md:max-h-70",
                    )}
                    alt="An image from a TED video"
                    onLoad={() => {
                      mixpanel.track_pageview({
                        game_id: gameData?.id,
                        page: "Results",
                      });
                      mixpanel.track_pageview({
                          game_id: gameData?.id,
                        page: `Pixellence: Game Results`,
                      });
                    }}
                  />
                </a>
                <div // Play button
                  className={cn(
                    "absolute",
                    "w-20 h-20",
                    "rounded-full",
                    "bg-white opacity-50",
                    "shadow-[0_1px_3px_2px_rgba(0,0,0,0.1)]",
                    "flex justify-center items-center",
                    "transition-transform duration-75",
                    "group-hover:scale-105 group-active:scale-105 group-hover:opacity-80 group-active:opacity-80",
                    "pointer-events-none",
                  )}
                >
                  <IconPlay width={"100"} height={"100"} fill="black" />
                </div>
              </div>
              <div // Video data container
                className={cn(
                  "flex-1 flex flex-col justify-center",
                  "min-h-23 md:min-h-24",
                  "p-1 pb-2",
                  "text-left text-sm md:text-base",
                  videoMetadataLoading && "opacity-0",
                  "transition-opacity duration-100",
                )}
              >
                <p className="">As seen in the TED-Ed animation</p>
                <a // Video title link
                  className={cn(
                    "underline decoration-1 underline-offset-1.5 italic",
                    "mb-1",
                    "text-black hover:text-blue-mid",
                  )}
                  href={videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {videoTitle || "Learn more on TED."}
                </a>
                {videoTitle && (
                  <p className="text-sm">
                    {`${formatVideoViews(videoViewCount)} plays`}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div // Share container
            className={cn(
              "px-4 py-2.5 md:py-4 pb-4 md:pb-5",
              "flex flex-col items-center md:justify-center gap-1.5 md:gap-2",
              "text-sm md:text-md",
              "bg-blue-light shadow-[0_1px_5px_2px_rgba(0,0,0,0.1)]",
            )}
          >
            <p className="mb-0.5 md:mb-1">
              Finished in <span className="font-bold">{formattedGameTime}</span>{" "}
              {getTimeDescriptor(formattedGameTime)}.
            </p>
            <ShareButton className="w-40" />
            <MenuButton
              className="text-sm w-40 h-9 md:h-10"
              onClick={handleBackToGridClick}
            >
              Back to Grid
            </MenuButton>
          </div>
        </div>
        <div className="flex flex-col gap-2 md:gap-3">
          <more-games-button className="[&::part(button)]:bg-black [&::part(button)]:max-w-none [&::part(button)]:w-full md:hidden"></more-games-button>
          <CountDownTimer
            className="text-sm"
            text="New game in"
            targetDate={new Date(today.setHours(24, 0, 0, 0))}
          />
          <NewsletterSignupBanner />
          {/* <NPSFeedbackDialog id={gameId} /> */}
        </div>
        <div className="hidden w-1000 bg-white md:mt-4 md:block md:pt-4 md:pb-8">
          <all-games></all-games>
        </div>
        {/* Leaving this here in case we want to add logos */}
        {/* <div // LOGOS
          className={cn(
            "flex justify-center items-center gap-4",
            "max-h-15 mb-2",
            "opacity-20",
          )}
        >
          <img src={tedLogo} className="w-[3%] min-w-10" />
          <img src={tfLogo} className="w-[6%] min-w-14" />
        </div> */}
      </div>
    </div>
  );
}

function formatVideoViews(views: number): string {
  if (views >= 1000000) {
    return (views / 1000000).toFixed(1) + "M";
  }
  return views.toLocaleString();
}
